import {
  campaignPeriodStatusSchema,
  DONATION_EVIDENCE_STAGES,
  DONATION_UNFULFILL_REASON_MAX_LENGTH,
  DONATION_UNFULFILL_REASON_MIN_LENGTH,
  DONATION_UNFULFILL_REFUSALS,
  donationStatusSchema,
  OPERATION_STAGES,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { bulkResultSchema, bulkRowSchema, listRequestSchema } from './common/list.js';
import { moneySchema } from './common/money.js';
import { paginated, sortDirectionSchema } from './common/pagination.js';
import { printoutListRefSchema } from './printout-list.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const timestampSchema = z.iso.datetime({ offset: true });

export const operationStageSchema = z.enum(OPERATION_STAGES);
export const donationEvidenceStageSchema = z.enum(DONATION_EVIDENCE_STAGES);

/**
 * One campaign the caller may carry work out for — by a role that holds
 * `donation.fulfill`, or by a campaign staff assignment in force
 * (docs/decisions/0090) — with what waits in their reach: the menu's badge and
 * the operations page's header. `institutions` are the places its work in the
 * caller's reach is carried out at, the page's institution selector.
 */
export const operationCampaignSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  categoryName: z.string(),
  categoryIcon: z.string().nullable(),
  unitLabel: z.string().nullable(),
  /** Null is a campaign whose order holds no animals: every donation is its own work. */
  unitsPerGroup: z.number().int().nullable(),
  /** The caller reaches it through a staff assignment in force. */
  staffed: z.boolean(),
  periods: z.array(
    z.object({
      id: z.uuid(),
      seasonYear: z.number().int().nullable(),
      status: campaignPeriodStatusSchema,
    }),
  ),
  counts: z.object({
    /** The waiting list: `pending` and ready for the order. */
    waiting: z.number().int().nonnegative(),
    queued: z.number().int().nonnegative(),
  }),
  institutions: z.array(z.object({ id: z.uuid(), name: z.string() })),
});

export type OperationCampaign = z.infer<typeof operationCampaignSchema>;

/** The orderings a stage may be read in; each ends in the reference code. */
export const OPERATION_SORT_FIELDS = [
  'donatedAt',
  'queuePosition',
  'fulfilledAt',
  'referenceCode',
] as const;

export type OperationSortField = (typeof OPERATION_SORT_FIELDS)[number];

/**
 * **A campaign's operations page is its own read, not a filter of
 * `donation.list`** (docs/decisions/0091): the campaign is the route's, the
 * stage is required, and the rows are read through the caller's reach for
 * `donation.fulfill`. Every other id narrows and never widens: a period, a
 * work institution or an evidence stage outside what the caller reads matches
 * nothing. Without `sortBy`, a stage reads in its own order — the waiting list
 * oldest first, the order by its position, the work carried out newest first.
 */
export const operationListInputSchema = listRequestSchema.extend({
  campaignId: z.uuid(),
  stage: operationStageSchema,
  campaignPeriodId: z.uuid().optional(),
  /** Where the work is carried out — the yurt, falling back to the entry institution. */
  workInstitutionId: z.uuid().optional(),
  /** Only on `fulfilled`: where its evidence stands. Ignored on the other stages. */
  evidence: donationEvidenceStageSchema.optional(),
  donatedFrom: timestampSchema.optional(),
  donatedTo: timestampSchema.optional(),
  /** On an active PDF list, or on none — docs/decisions/0092. */
  printoutList: z.enum(['listed', 'unlisted']).optional(),
  sortBy: z.enum(OPERATION_SORT_FIELDS).optional(),
  sortDir: sortDirectionSchema.optional(),
});

export type OperationListInput = z.infer<typeof operationListInputSchema>;

/**
 * One donation as the operations page shows it: who and what the work is for,
 * where it is planned and where it was done, its animals when its campaign
 * cuts them, and — once carried out — where its evidence stands. No money and
 * no payment status: the page carries work out, and the money is the donation
 * list's (docs/decisions/0091).
 */
export const operationDonationSchema = z.object({
  id: z.uuid(),
  referenceCode: z.string(),
  campaignPeriodId: z.uuid(),
  seasonYear: z.number().int().nullable(),
  status: donationStatusSchema,
  readyForQueue: z.boolean(),
  donorName: z.string().nullable(),
  donorIsAnonymous: z.boolean(),
  fromDonationBox: z.boolean(),
  onBehalfOfName: z.string().nullable(),
  quantity: z.number().int().nullable(),
  unitLabel: z.string().nullable(),
  donatedAt: z.string(),
  queuePosition: z.number().int().nullable(),
  queuedAt: z.string().nullable(),
  institutionId: z.uuid(),
  institutionName: z.string(),
  /** The planned yurt, falling back to the entry institution — the display name a season gives it. */
  workInstitutionId: z.uuid(),
  workInstitutionName: z.string(),
  fulfilledInstitutionName: z.string().nullable(),
  fulfilledAt: z.string().nullable(),
  fulfillmentCost: moneySchema.nullable(),
  /**
   * The active PDF list the donation's printout was taken in, or null — the
   * page's *PDF Listesi* column (docs/decisions/0092). `readable` says whether
   * the reader may open it: a list another person made on work the reader is
   * not assigned to is named, never linked (docs/decisions/0097).
   */
  printoutList: printoutListRefSchema
    .extend({ readable: z.boolean(), name: z.string().nullable().optional() })
    .nullable(),
  evidence: z.object({
    /** Null until the donation is carried out. */
    stage: donationEvidenceStageSchema.nullable(),
    /** Files attached to the donation and inbound media matched to it. */
    items: z.number().int().nonnegative(),
  }),
  /**
   * The animals its shares fall in, numbered over the whole period's order as
   * the order numbers them, and which were carried out — null outside the order
   * or where the campaign cuts no animals. A share of an animal is carried out
   * with its whole animal (docs/decisions/0025, 0091).
   */
  animals: z
    .object({
      fulfilled: z.number().int(),
      total: z.number().int(),
      groups: z.array(
        z.object({
          group: z.number().int(),
          shares: z.number().int(),
          size: z.number().int(),
          fulfilled: z.boolean(),
        }),
      ),
    })
    .nullable(),
});

export type OperationDonation = z.infer<typeof operationDonationSchema>;

/** A selection named by its ids, at most a hundred, each once. */
const operationIdsSchema = z
  .array(z.uuid())
  .min(1)
  .max(100)
  .refine((ids) => new Set(ids).size === ids.length, 'a donation appears once in one request');

/**
 * One row of a one-click fulfilment. `UNCHANGED` is a donation already carried
 * out — a second click, or somebody else a moment earlier — and counts as done.
 * `DONATION_ANIMAL_NOT_QUEUED` is a waiting share of a campaign that cuts
 * animals: it has no animal until it is taken into the order.
 * `groups` are the animals this row's click recorded, and `alsoCompleted` the
 * other donations those animals completed.
 */
export const operationFulfilResultSchema = bulkRowSchema([
  'NOT_FOUND',
  'PERMISSION_DENIED',
  'OUT_OF_SCOPE',
  'UNCHANGED',
  'DONATION_INVALID_TRANSITION',
  'DONATION_FULFILLMENT_PLACE_REQUIRED',
  'DONATION_GROUP_EMPTY',
  'DONATION_ANIMAL_NOT_QUEUED',
]).extend({
  status: donationStatusSchema.nullable(),
  groups: z.array(z.number().int()),
  alsoCompleted: z.number().int().nonnegative(),
});

export type OperationFulfilResult = z.infer<typeof operationFulfilResultSchema>;

/**
 * One row of an undo. `UNCHANGED` is a donation not carried out any more — a
 * second click. `alsoReverted` are the other donations that went back with
 * the animals this row's undo took back.
 */
export const operationUnfulfilResultSchema = bulkRowSchema([
  'NOT_FOUND',
  'PERMISSION_DENIED',
  'OUT_OF_SCOPE',
  'UNCHANGED',
  'DONATION_INVALID_TRANSITION',
  ...DONATION_UNFULFILL_REFUSALS,
]).extend({
  status: donationStatusSchema.nullable(),
  groups: z.array(z.number().int()),
  alsoReverted: z.number().int().nonnegative(),
});

export type OperationUnfulfilResult = z.infer<typeof operationUnfulfilResultSchema>;

/** Required, ten characters at least once trimmed, as a deletion's is — docs/decisions/0091. */
const unfulfilReasonSchema = z
  .string()
  .trim()
  .min(DONATION_UNFULFILL_REASON_MIN_LENGTH)
  .max(DONATION_UNFULFILL_REASON_MAX_LENGTH);

/**
 * The operations routes, nested under `donation` beside the donation routes
 * they share their permissions and their reach with — docs/decisions/0091.
 */
export const donationOperationsContract = {
  /** The campaigns the caller operates on, with the work waiting in their reach. */
  operationCampaigns: oc
    .route({ method: 'GET', path: '/operations/campaigns' })
    .errors(guarded)
    .input(z.object({}))
    .output(z.object({ items: z.array(operationCampaignSchema) })),

  operationList: oc
    .route({ method: 'GET', path: '/operations/campaigns/{campaignId}/donations' })
    .errors(guarded)
    .input(operationListInputSchema)
    .output(paginated(operationDonationSchema)),

  /**
   * A fulfilment in one click, no form: carried out at the planned yurt, now,
   * with no cost. A share of an animal carries its **whole animal** out, every
   * animal of the donation not yet cut, through the order's own group
   * fulfilment under the period's lock. One transaction per row; a row already
   * carried out answers `UNCHANGED`, so a second click, or two people at once,
   * never carries anything out twice.
   */
  operationFulfil: oc
    .route({ method: 'POST', path: '/operations/fulfilments' })
    .errors(guarded)
    .input(z.object({ ids: operationIdsSchema }))
    .output(bulkResultSchema(operationFulfilResultSchema)),

  /**
   * The undo of a fulfilment, behind `donation.unfulfill` alone — never a
   * derived permission — and with a reason. A donation goes back to the order,
   * or to waiting when it never held a place; a share of a carried-out animal
   * takes its animal back, and every donation that animal completed goes back
   * with it. Refused after a cancelled remainder and once its evidence was
   * offered to a party.
   */
  operationUnfulfil: oc
    .route({ method: 'POST', path: '/operations/unfulfilments' })
    .errors(guarded)
    .input(z.object({ ids: operationIdsSchema, reason: unfulfilReasonSchema }))
    .output(bulkResultSchema(operationUnfulfilResultSchema)),
};
